import type { FrontUnit } from '../maps/frontsMath'
import type { SeriesFrame } from './seriesCatalog'
import { WAVE_STEP_DEFAULT } from './waveContours'

/**
 * Значения, которыми серия подменяет вид на время съёмки кадра.
 *
 * Почему подмена, а не запись в общий контекст:
 *
 *  1. **Возврат вида атомарен.** Снять серию — это обнулить ОДИН объект.
 *     Восстанавливая семь значений по одному, мы завели бы семь способов
 *     застрять на полпути: упавший третий кадр оставил бы человеку изотерму
 *     9,5 °C, которую он не выбирал.
 *  2. **Адресная строка остаётся пользовательской.** Ключи `iso`, `front`,
 *     `isobath`, `wavestep`, `depth` пишет эффект синхронизации URL. Пиши серия
 *     в контекст — «Поделиться» посреди прогона отдал бы чужой вид, а
 *     перезагрузка открыла бы кадр серии вместо рабочей карты.
 *  3. **Глубину менять по-настоящему нельзя.** `depth` — зависимость эффекта
 *     фонового слоя раздела: он перезапустился бы посреди серии и показал свой
 *     слой поверх кадра.
 *
 * Тем же объектом описывается кадр в `parametry.txt` — источник один, и
 * разойтись файлу с картинкой негде.
 */
export interface ToolsView {
  depth: number
  /** Знает ли слой кадра глубину. Батиметрии и апвеллингу пишем словами. */
  depthApplicable: boolean
  isotherm: number | null
  /** Поверхностная изотерма кадра (0 м); null — выключена. */
  isothermSurface: number | null
  /** Шаг смежных изотерм кадра; null — выключены. */
  isothermSpread: number | null
  fronts: number | null
  frontsUnit: FrontUnit
  isobathStep: number | null
  waveOverlay: boolean
  waveStep: number
  hsiDrift: boolean
  /** Слой заливки кадра. Нужен плашке: шкалу растра она обязана взять у КАДРА,
   *  а не у активной модели раздела. */
  layer: string
  /** Оверлеи кадра. Кадр замещает вид ЦЕЛИКОМ — значит и набор оверлеев тоже:
   *  оставив пользовательские, мы положили бы на кадр «Лёд» ещё и его рубежи с
   *  промрайонами, а на кадр слоя — всё, что человек забыл выключить. */
  overlays: string[]
  /** Ключ подписи слоя для плашки; null — подпись берёт раздел, как обычно. */
  layerLabelKey: string | null
}

/**
 * Что показывать сейчас. Кадр серии замещает вид ЦЕЛИКОМ, а не сливается с ним
 * по полям: слияние оставило бы на кадре «Апвеллинг» пользовательскую изотерму —
 * ту самую чужую линию, ради отсутствия которой кадры 2–5 и заведены пустыми.
 */
export function effectiveTools(user: ToolsView, override: ToolsView | null): ToolsView {
  return override ?? user
}

/** Кадр реестра + вид пользователя → значения на время съёмки этого кадра. */
export function frameOverride(frame: SeriesFrame, base: { depth: number }): ToolsView {
  return {
    depth: frame.depth ?? base.depth,
    depthApplicable: frame.depth != null,
    isotherm: frame.isotherm,
    isothermSurface: frame.isothermSurface,
    isothermSpread: frame.isothermSpread,
    fronts: frame.fronts,
    frontsUnit: frame.frontsUnit,
    isobathStep: frame.isobathStep,
    waveOverlay: frame.waveStep != null,
    // Шаг значим только при включённом оверлее, но пустое значение сломало бы
    // ползунок, если человек откроет его сразу после серии.
    waveStep: frame.waveStep ?? WAVE_STEP_DEFAULT,
    hsiDrift: frame.hsiDrift,
    layer: frame.layer,
    overlays: frame.overlays,
    layerLabelKey: frame.labelKey,
  }
}


/**
 * Слой, чью ШКАЛУ описывает плашка кадра.
 *
 * У кадра оверлея это сам оверлей, а не подложка: подложка (батиметрия) стоит
 * там для читаемости, а величина, которую на картинке читают, — оверлейная.
 * Шкала глубин под картинкой льда была бы правдоподобной и неверной.
 */
export function scaleLayerOf(view: ToolsView): string {
  return view.overlays[0] ?? view.layer
}
